import { create } from "zustand";

import { PIPELINE_STAGES, type PipelineStage } from "@/services/debugpavilion";

/** AI 面板四模式（ARCHITECTURE_DESIGN.md §5.3）。 */
export type AiPanelMode = "plan" | "design" | "craft" | "chat";

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  source?: string;
}

export interface PlanSession {
  requirement: string;
  status: "idle" | "generating" | "ready" | "error";
  /** generate 返回/确认后的应用 id（用于"确认并生成 AppSchema"后回写画布） */
  appId?: string;
  specMd?: string;
  error?: string;
}

export interface DesignSession {
  requirement: string;
  status: "idle" | "generating" | "ready" | "error";
  appId?: string;
  error?: string;
}

export type CraftStepState = "pending" | "active" | "done" | "error";

export interface CraftStep {
  /** 与后端 pipeline_stage 对齐：recon/foundation/specs/parallel_build/assembly_qa */
  id: PipelineStage;
  label: string;
  status: CraftStepState;
}

export interface CraftSession {
  requirement: string;
  status: "idle" | "running" | "completed" | "failed";
  generationId?: string;
  appId?: string;
  steps: CraftStep[];
  reviewScore: number | null;
  testResults: Record<string, unknown> | null;
  error?: string;
}

/** AI 面板与 builder 页面的桥接 props（画布① 热更新依赖 savedAppId）。 */
export interface AiPanelBridge {
  /** 当前已保存的应用 id（未保存草稿为 null） */
  savedAppId?: string | null;
  /** generate 新建应用时回调，builder 用它记录 savedAppId */
  onAppCreated?: (appId: string) => void;
}

export const CRAFT_STEP_LABELS: Record<PipelineStage, string> = {
  recon: "需求侦察",
  foundation: "工程基座",
  specs: "模块规格",
  parallel_build: "并行构建",
  assembly_qa: "装配质检",
};

function createInitialSteps(): CraftStep[] {
  return PIPELINE_STAGES.map((stage) => ({
    id: stage,
    label: CRAFT_STEP_LABELS[stage],
    status: "pending" as const,
  }));
}

interface AiPanelState {
  /** 当前激活模式 */
  mode: AiPanelMode;
  setMode: (mode: AiPanelMode) => void;

  /** Chat 模式会话历史（切换模式不丢失） */
  chatMessages: ChatMessage[];
  appendChatMessage: (message: ChatMessage) => void;
  resetChat: () => void;

  /** Plan 模式会话 */
  plan: PlanSession | null;
  setPlan: (session: PlanSession | null) => void;

  /** Design 模式会话 */
  design: DesignSession | null;
  setDesign: (session: DesignSession | null) => void;

  /** Craft 模式会话（5 阶段流水线进度状态机） */
  craft: CraftSession | null;
  startCraft: (requirement: string, generationId: string, appId?: string) => void;
  updateCraftStage: (stage: PipelineStage) => void;
  finishCraft: (summary: { reviewScore?: number | null; testResults?: Record<string, unknown> | null }) => void;
  failCraft: (error: string) => void;
  resetCraft: () => void;
}

export const useAiPanelStore = create<AiPanelState>((set) => ({
  mode: "plan",
  setMode: (mode) => set({ mode }),

  chatMessages: [
    {
      role: "assistant",
      content: "你好，我是罢歌亭 AI 副驾驶。可以解答编程问题或帮你分析当前应用。",
    },
  ],
  appendChatMessage: (message) => set((state) => ({ chatMessages: [...state.chatMessages, message] })),
  resetChat: () => set({ chatMessages: [] }),

  plan: null,
  setPlan: (session) => set({ plan: session }),

  design: null,
  setDesign: (session) => set({ design: session }),

  craft: null,
  startCraft: (requirement, generationId, appId) =>
    set({
      craft: {
        requirement,
        status: "running",
        generationId,
        appId,
        steps: createInitialSteps(),
        reviewScore: null,
        testResults: null,
      },
    }),
  updateCraftStage: (stage) =>
    set((state) => {
      if (!state.craft) return state;
      const stageIndex = PIPELINE_STAGES.indexOf(stage);
      const steps = state.craft.steps.map((step, index) => {
        if (index < stageIndex) return { ...step, status: "done" as const };
        if (index === stageIndex) return { ...step, status: "active" as const };
        return { ...step, status: "pending" as const };
      });
      return { craft: { ...state.craft, steps } };
    }),
  finishCraft: (summary) =>
    set((state) => {
      if (!state.craft) return state;
      return {
        craft: {
          ...state.craft,
          status: "completed",
          reviewScore: summary.reviewScore ?? state.craft.reviewScore,
          testResults: summary.testResults ?? state.craft.testResults,
          steps: state.craft.steps.map((step) => ({ ...step, status: "done" as const })),
        },
      };
    }),
  failCraft: (error) =>
    set((state) => {
      if (!state.craft) return state;
      const hasActive = state.craft.steps.some((step) => step.status === "active");
      return {
        craft: {
          ...state.craft,
          status: "failed",
          error,
          steps: state.craft.steps.map((step) =>
            hasActive
              ? step.status === "active"
                ? { ...step, status: "error" as const }
                : step
              : { ...step, status: "pending" as const },
          ),
        },
      };
    }),
  resetCraft: () => set({ craft: null }),
}));

// 暴露 getState 便于测试直接断言（zustand 惯例）。
export const getAiPanelStore = () => useAiPanelStore.getState();
